/**
 * Referral credit clients asked to take in USDT (owner, 2026-10-29). The owner
 * transfers inside Bybit to the UID shown, then marks the request paid — with
 * the password, because it is money. Nothing here moves money.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { decideReferralPayout, readReferralPayouts, type ReferralPayoutResponse } from "@/shared/api";
import { money } from "@/shared/money/money";

const KEY = ["referral-payouts"] as const;
const FIELD =
  "min-h-9 rounded-row border border-border-strong bg-surface-default px-2 text-text-primary focus-visible:outline-2 focus-visible:outline-focus-ring";

function Request({ item }: { readonly item: ReferralPayoutResponse }): React.JSX.Element {
  const queries = useQueryClient();
  const [password, setPassword] = useState("");
  // Irreversible either way: the choice is said back before it is made.
  const [choice, setChoice] = useState<boolean | null>(null);
  const decide = useMutation({
    mutationFn: async (paid: boolean) => {
      const { response } = await decideReferralPayout({
        path: { payout_id: item.payout_id },
        body: { paid, current_password: password },
      });
      if (response?.status !== 204) throw new Error(response?.status === 403 ? "password" : "refused");
    },
    onSuccess: async () => { await queries.invalidateQueries({ queryKey: KEY }); },
    onSettled: () => { setPassword(""); setChoice(null); },
  });
  return (
    <li className="flex flex-col gap-2 border-b border-border-subtle py-3 last:border-b-0" data-testid="payout-request">
      <span className="text-text-primary">
        <b>{item.display_name}</b> — {money(item.amount)} USDT на Bybit UID <span className="font-mono">{item.exchange_uid}</span>
      </span>
      <span className="text-helper-sm text-text-muted">запрошено {new Date(item.requested_at).toLocaleString("ru-RU")}</span>
      {choice === null ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="primary" onClick={() => { setChoice(true); }}>Выплачено</Button>
          <Button type="button" onClick={() => { setChoice(false); }}>Отклонить</Button>
        </div>
      ) : (
        <form className="flex flex-col gap-2" onSubmit={(event) => { event.preventDefault(); if (password !== "" && !decide.isPending) decide.mutate(choice); }}>
          <p role="status" className="text-body-main text-text-primary" data-testid="payout-confirm">
            {choice
              ? `Вы перевели ${money(item.amount)} USDT на UID ${item.exchange_uid}? Отметка необратима.`
              : `Отклонить заявку на ${money(item.amount)} USDT? Бонус вернётся клиенту. Решение необратимо.`}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input aria-label="Ваш пароль" className={`${FIELD} w-40`} type="password" autoComplete="current-password" value={password} onChange={(event) => { setPassword(event.target.value); }} />
            <Button type="submit" variant="primary" disabled={password === "" || decide.isPending}>{choice ? "Да, выплачено" : "Да, отклонить"}</Button>
            <Button type="button" onClick={() => { setChoice(null); setPassword(""); }}>Назад</Button>
          </div>
        </form>
      )}
      {decide.isError && <span role="alert" className="text-helper-sm text-status-warning">{decide.error.message === "password" ? "Пароль не подошёл." : "Заявка уже решена."}</span>}
    </li>
  );
}

export function ReferralPayouts(): React.JSX.Element | null {
  const query = useQuery({
    queryKey: KEY,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data } = await readReferralPayouts({ cache: "no-store" });
      if (data === undefined) throw new Error("payouts unavailable");
      return data;
    },
  });
  if (query.data === undefined || query.data.length === 0) return null;
  return (
    <Panel title={`Заявки на выплату бонуса · ${String(query.data.length)}`} testId="referral-payouts">
      <p className="text-helper-sm text-text-secondary">Переведите сумму внутри Bybit на указанный UID, затем отметьте «Выплачено».</p>
      <ul>{query.data.map((item) => <Request key={item.payout_id} item={item} />)}</ul>
    </Panel>
  );
}
